/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @flow
import includes from 'lodash/includes'
import PropTypes from 'prop-types'
import * as React from 'react'
import { toast } from 'react-toastify'
import { v4 as uuid } from 'uuid'

import { ResourceResponseContext } from '@/components/ResourceFill/providers/ResourceResponseProvider'
import { useSaveAnswerSignature } from '@/queries/answers/answerSignature/useSaveAnswerSignature'
import { Answer } from '@/types/answer'
import { Page } from '@/types/page'
import type { Question } from '@/types/question'
import { SignatureQuestionRender } from '@/ui/SignatureQuestionRender'
import { hasText } from '@/utils/helpers'

import ZentakeFormContext from '../ZentakeFormContext'

type Props = {
  question: Question
  isOfficeUse: boolean
  page: Page
}

type SignatureAnswer = {
  id: number | string
  image: File | null
  text: string | null
  untrimmedImage: File | null
  ip?: string | null
  updated_at?: string | null
  ip_address?: string | null
  created_at?: string | null
}

function SignatureQuestion({ question, isOfficeUse, page }: Props) {
  const formContext = React.useContext(ZentakeFormContext)
  const {
    isQuestionReadonly,
    isQuestionDirty,
    isQuestionValid,
    getAnswerSignature,
    onAnswerSignatureChanged,
    willShowNotAllowedCursor,
    getQuestionError,
    signatureMemo,
    mode,
  } = formContext

  const { response } = React.useContext(ResourceResponseContext) || {}
  const readonly = isQuestionReadonly(question)
  const showNotAllowedCursor = willShowNotAllowedCursor(question)
  const { saveAnswerSignature } = useSaveAnswerSignature()

  const isDirty = isQuestionDirty(question)
  const isValid = isQuestionValid(question)
  const questionError = getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  const answer = getAnswerSignature(question)
  const onReset = React.useCallback(() => {
    onAnswerSignatureChanged(question, {
      id: answer?.id,
      image: null,
      text: null,
      untrimmedImage: null,
      ip: null,
      updated_at: null,
      ip_address: null,
      created_at: null,
      answer_raw: '',
      answer_text: null,
      answer_files: [],
    } as unknown as Answer)
  }, [answer?.id, onAnswerSignatureChanged, question])

  const memorizeSignature = React.useCallback(
    (answerToSave: SignatureAnswer) => {
      if (!question.client_signature) return
      // @ts-ignore
      signatureMemo?.setMemorizedSignature(answerToSave)
    },
    [question.client_signature, signatureMemo]
  )

  const onConfirm = React.useCallback(
    async (answerToSave: SignatureAnswer) => {
      const isRepeatableSection = page?.repeat_section
      try {
        const signature = await saveAnswerSignature({
          responseId: response?.id ?? '',
          imageFile: answerToSave.image,
          text: answerToSave.text,
          mode: mode,
          questionId: question.id,
          repeat_section_page_id: isRepeatableSection ? uuid() : null,
          repeat_section_original_page_id: isRepeatableSection
            ? page?.id
            : null,
          repeat_section_order: isRepeatableSection ? question.order : null,
        })
        onAnswerSignatureChanged(question, {
          ...answerToSave,
          ip_address: signature?.ip_address ?? '',
          created_at: signature?.created_at ?? '',
          updated_at: signature?.created_at ?? '',
        } as unknown as Answer)
        memorizeSignature(answerToSave)
      } catch (error: any) {
        if (includes(error.message, 'Network Error')) {
          toast.error(
            'Lost connection, please check your internet connection and try again.'
          )
        } else {
          toast.error(error.message)
        }
        throw error
      }
    },
    [
      memorizeSignature,
      mode,
      onAnswerSignatureChanged,
      question,
      response?.id,
      saveAnswerSignature,
      page,
    ]
  )

  const canUseProviderSignature = React.useMemo(
    () =>
      // @ts-ignore
      Boolean(signatureMemo?.memorizedSignature) &&
      isOfficeUse &&
      mode === 'office' &&
      question.is_office_use_only,
    [
      isOfficeUse,
      mode,
      // @ts-ignore
      signatureMemo?.memorizedSignature,
      question.is_office_use_only,
    ]
  )

  const canUseClientSignature = React.useMemo(() => {
    return (
      !isOfficeUse &&
      !question.is_office_use_only &&
      // @ts-ignore
      Boolean(signatureMemo?.memorizedSignature) &&
      mode !== 'office' &&
      question.client_signature
    )
  }, [
    isOfficeUse,
    mode,
    question.client_signature,
    question.is_office_use_only,
    // @ts-ignore
    signatureMemo?.memorizedSignature,
  ])

  const isWaitingForMemorizedAnswer = React.useMemo(() => {
    if (!canUseProviderSignature && !canUseClientSignature) return false
    // @ts-ignore
    if (!signatureMemo?.memorizedSignature) return false

    if (answer) return false

    // @ts-ignore
    const { memorizedSignature } = signatureMemo

    if (
      question.question_type.question_subtype === 'type' &&
      !memorizedSignature.text
    ) {
      return false
    }

    if (
      question.question_type.question_subtype === 'draw' &&
      !memorizedSignature.image
    ) {
      return false
    }

    return true
  }, [
    canUseProviderSignature,
    canUseClientSignature,
    question?.question_type?.question_subtype,
    signatureMemo,
    answer,
  ])

  const useMemorizedAnswer = React.useCallback(async () => {
    if (!canUseProviderSignature && !canUseClientSignature) return
    // @ts-ignore
    if (!signatureMemo?.memorizedSignature) return

    // @ts-ignore
    await onConfirm(signatureMemo.memorizedSignature)
  }, [
    canUseProviderSignature,
    onConfirm,
    canUseClientSignature,
    // @ts-ignore
    signatureMemo?.memorizedSignature,
  ])

  return (
    <SignatureQuestionRender
      answer={answer}
      errorMessage={errorMessage}
      forceShowPad={
        question.question_type?.question_subtype === 'all' &&
        isWaitingForMemorizedAnswer
      }
      hasError={hasError}
      isOfficeUse={isOfficeUse}
      isRequired={question.is_required}
      label={question.text}
      // @ts-ignore
      memorizedImage={signatureMemo?.memorizedSignature?.image}
      onConfirm={onConfirm}
      onReset={onReset}
      questionId={question.id.toString()}
      readOnlyClickAction={useMemorizedAnswer}
      readonly={readonly || isWaitingForMemorizedAnswer}
      showNotAllowedCursor={showNotAllowedCursor}
      showReadOnlyPlaceholder={isWaitingForMemorizedAnswer}
      subtype={question.question_type?.question_subtype}
    />
  )
}

SignatureQuestion.propTypes = {
  question: PropTypes.object.isRequired,
}

export default SignatureQuestion
